import { FetchService } from '@affine/core/modules/cloud';
import {
  HelpActionsService,
  sanitizeFeedbackPrefill,
} from '@affine/core/modules/help-actions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { I18n } from '@affine/i18n';
import type { DocFrontend } from '@affine/nbstore';
import type {
  DatabaseRepairActions,
  DatabaseRepairOutcome,
  DatabaseRepairRequest,
  DatabaseRepairResult,
} from '@blocksuite/affine/blocks/database';
import { collectDatabaseStorageSyncDocIds } from '@streamlain/database-identity';
import type { FrameworkProvider } from '@toeverything/infra';
import { firstValueFrom } from 'rxjs';

/** Same bound as the server repair endpoint. */
const MAX_REPAIR_DOCS = 64;

const SERVER_OUTCOMES = new Set<DatabaseRepairOutcome>([
  'requeued',
  'persisting',
  'root_not_received',
  'healthy',
]);

type RepairDocEngine = Pick<
  DocFrontend,
  'docState$' | 'repairDoc' | 'pullRemote'
>;

export type DatabaseRepairClientDoc = {
  docId: string;
  role: 'root' | 'rows';
  localPresent: boolean;
  synced: boolean;
  retrying: boolean;
  failureClass?: string;
  attempts?: number;
  reconcile?: { success: boolean; diffSize: number; error?: string };
};

export function collectRepairDocIds(request: DatabaseRepairRequest): string[] {
  return [
    ...new Set([
      ...request.docIds,
      ...collectDatabaseStorageSyncDocIds({
        databaseId: request.databaseId,
        workspaceId: request.workspaceId,
      }),
    ]),
  ].slice(0, MAX_REPAIR_DOCS);
}

async function readClientDoc(
  engine: RepairDocEngine,
  docId: string
): Promise<DatabaseRepairClientDoc> {
  const state = await firstValueFrom(engine.docState$(docId));
  return {
    docId,
    role: docId.endsWith('$root') ? 'root' : 'rows',
    localPresent: state.ready,
    synced: state.synced,
    retrying: state.syncRetrying,
    failureClass: state.syncIsolatedFailure?.failureClass,
    attempts: state.syncIsolatedFailure?.attempts,
  };
}

/**
 * Browser half of a database restore: every document retries now (ending a
 * self-healing backoff) and pushes what the server lacks. Errors are kept as
 * diagnostics, never thrown.
 */
export async function repairClientDocs(
  engine: RepairDocEngine,
  docIds: readonly string[]
): Promise<DatabaseRepairClientDoc[]> {
  return Promise.all(
    docIds.map(async docId => {
      const doc = await readClientDoc(engine, docId);
      try {
        const [result] = await engine.repairDoc(docId);
        if (!result) return doc;
        return {
          ...doc,
          reconcile: {
            success: result.reconcile.success,
            diffSize: result.reconcile.diffSize,
            error: sanitizeFeedbackPrefill(result.reconcile.error, 500),
          },
        };
      } catch (error) {
        return {
          ...doc,
          reconcile: {
            success: false,
            diffSize: 0,
            error: sanitizeFeedbackPrefill(
              error instanceof Error ? error.message : String(error),
              500
            ),
          },
        };
      }
    })
  );
}

export function createDatabaseRepairActions(
  framework: FrameworkProvider
): DatabaseRepairActions {
  const engine = () => framework.get(WorkspaceService).workspace.engine.doc;

  return {
    restore: async (request): Promise<DatabaseRepairResult> => {
      const docIds = collectRepairDocIds(request);
      const docs = await repairClientDocs(engine(), docIds);
      const response = await framework
        .get(FetchService)
        .fetch('/api/repair-actions/database', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            workspaceId: request.workspaceId,
            databaseId: request.databaseId,
            routePath: location.pathname,
            appVersion: BUILD_CONFIG.appVersion,
            userAgent: navigator.userAgent.slice(0, 512),
            client: { missing: request.missing, docs },
          }),
        });
      if (!response.ok) return { outcome: 'failed', reportId: null };
      const { outcome, reportId } = (await response.json()) as {
        outcome: DatabaseRepairOutcome;
        reportId: string;
      };
      // Released server rows persist now; pull them back into the browser.
      for (const docId of docIds) {
        void engine()
          .pullRemote(docId)
          .catch(() => undefined);
      }
      return SERVER_OUTCOMES.has(outcome)
        ? { outcome, reportId }
        : { outcome: 'failed', reportId: null };
    },
    confirmRestored: (reportId, elapsedMs) => {
      void framework
        .get(FetchService)
        .fetch(
          `/api/repair-actions/reports/${encodeURIComponent(reportId)}/restored`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ elapsedMs: Math.round(elapsedMs) }),
          }
        )
        .catch(() => undefined);
    },
    reportBug: request => {
      const docIds = collectRepairDocIds(request);
      void Promise.all(docIds.map(docId => readClientDoc(engine(), docId)))
        .catch(() => [])
        .then(docs => {
          framework.get(HelpActionsService).reportBug({
            title: I18n.t('com.streamlain.dataview.database.dataMissing.title'),
            errorStack: JSON.stringify(
              {
                repairAction: 'database.report',
                databaseId: request.databaseId,
                missing: request.missing,
                docs,
              },
              null,
              2
            ),
          });
        });
    },
  };
}
